'use client';

import React, { useState, useEffect } from 'react';
import Link from 'next/link';
import { X } from 'lucide-react';

// Design tokens only (DESIGN.md): paper/ink surfaces, marigold primary, marigold-deep focus ring. Weights 400/600.
const FOCUS =
  'focus-visible:outline focus-visible:outline-[3px] focus-visible:outline-offset-[3px] focus-visible:outline-marigold-deep';
const PRIMARY = `rounded-control border-b-2 border-marigold-deep bg-marigold font-semibold text-marigold-ink transition hover:-translate-y-px active:translate-y-0 ${FOCUS}`;
const SECONDARY = `rounded-control border-2 border-ink bg-transparent font-semibold text-ink transition hover:bg-paper ${FOCUS}`;
const TEXT_LINK = `underline underline-offset-4 decoration-marigold-deep text-ink hover:text-ink-soft ${FOCUS}`;
const TOGGLE =
  "relative h-6 w-10 rounded-full bg-line transition-colors peer-checked:bg-ink peer-focus-visible:outline peer-focus-visible:outline-[3px] peer-focus-visible:outline-offset-2 peer-focus-visible:outline-marigold-deep after:absolute after:left-[2px] after:top-[2px] after:h-5 after:w-5 after:rounded-full after:bg-paper-raised after:transition-all after:content-[''] peer-checked:after:translate-x-full";

export default function CookieConsent() {
  const [showBanner, setShowBanner] = useState(false);
  const [hasInteracted, setHasInteracted] = useState(false);
  const [isManageOpen, setIsManageOpen] = useState(false);
  const [analyticsConsent, setAnalyticsConsent] = useState(false);
  const [marketingConsent, setMarketingConsent] = useState(false);

  useEffect(() => {
    // Check if user has already made a choice
    const cookieConsent = localStorage.getItem('cookieConsent');
    if (!cookieConsent) {
      // Add a small delay so it doesn't appear jarring on page load
      setTimeout(() => setShowBanner(true), 1500);
    } else {
      setHasInteracted(true);
    }
  }, []);

  const handleAcceptAll = () => {
    localStorage.setItem('cookieConsent', JSON.stringify({
      necessary: true,
      analytics: true,
      marketing: true,
      timestamp: new Date().toISOString()
    }));
    setShowBanner(false);
    setHasInteracted(true);

    // Initialize analytics services here
    // Example: initializeGoogleAnalytics();
  };

  const handleAcceptNecessary = () => {
    localStorage.setItem('cookieConsent', JSON.stringify({
      necessary: true,
      analytics: false,
      marketing: false,
      timestamp: new Date().toISOString()
    }));
    setShowBanner(false);
    setHasInteracted(true);
  };

  const handleManagePreferences = () => {
    setIsManageOpen(true);
  };

  const handleSavePreferences = () => {
    localStorage.setItem('cookieConsent', JSON.stringify({
      necessary: true,
      analytics: analyticsConsent,
      marketing: marketingConsent,
      timestamp: new Date().toISOString()
    }));
    setShowBanner(false);
    setHasInteracted(true);
    setIsManageOpen(false);
  };

  const handleClosePreferences = () => {
    setIsManageOpen(false);
  };

  if (!showBanner || hasInteracted) {
    return null;
  }

  return (
    <>
      {/* Friendly cookie consent banner - smaller and less intrusive */}
      <div
        data-cookie-consent=""
        className="fixed bottom-4 left-4 right-4 z-50 rounded-card border border-line bg-paper-raised text-ink shadow-[var(--shadow-photo)] transition-all duration-500 ease-out animate-slide-up md:bottom-6 md:left-auto md:right-6 md:max-w-md"
      >
        <div className="p-4">
          {/* Close button */}
          <button
            onClick={handleAcceptNecessary}
            className={`absolute right-2 top-2 rounded-control text-ink-muted transition-colors hover:text-ink ${FOCUS}`}
            aria-label="Close and accept necessary cookies only"
          >
            <X className="h-5 w-5" />
          </button>

          <div className="flex items-start gap-3">
            {/* Friendly cookie emoji */}
            <div className="flex-shrink-0 text-2xl animate-bounce-subtle" aria-hidden="true">🍪</div>

            <div className="flex-1">
              <h3 className="mb-1 text-sm font-semibold text-ink">
                Hey there! We use cookies
              </h3>
              <p className="mb-3 text-xs text-ink-soft">
                We use cookies to make your experience better and see how people use our site.
                <button onClick={handleManagePreferences} className={`ml-1 ${TEXT_LINK}`}>
                  Customize
                </button>
              </p>

              <div className="flex gap-2">
                <button onClick={handleAcceptAll} className={`min-h-[36px] flex-1 px-3 py-1.5 text-xs ${PRIMARY}`}>
                  Sounds good!
                </button>
                <button onClick={handleAcceptNecessary} className={`min-h-[36px] flex-1 px-3 py-1.5 text-xs ${SECONDARY}`}>
                  Just essentials
                </button>
              </div>

              <p className="mt-2 text-center text-xs text-ink-muted">
                Learn more in our{' '}
                <Link href="/legal/privacy" className={TEXT_LINK}>
                  Privacy Policy
                </Link>
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Preferences management modal - friendlier design */}
      {isManageOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-ink/40 p-4 backdrop-blur-sm animate-fade-in">
          <div className="max-h-[90vh] w-full max-w-md overflow-y-auto rounded-card bg-paper-raised text-ink shadow-[var(--shadow-book)] transition-all duration-300 animate-scale-up">
            <div className="p-6">
              <div className="mb-4 flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <span className="text-2xl" aria-hidden="true">🍪</span>
                  <h3 className="text-lg font-semibold text-ink">Cookie Settings</h3>
                </div>
                <button
                  onClick={handleClosePreferences}
                  className={`rounded-control text-ink-muted transition-colors hover:text-ink ${FOCUS}`}
                  aria-label="Close"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>

              <p className="mb-6 text-sm text-ink-soft">
                Choose which cookies you&apos;d like to accept. We promise to respect your choices!
              </p>

              <div className="space-y-4">
                {/* Necessary cookies - always enabled */}
                <div className="rounded-control bg-tile p-4">
                  <div className="flex items-center justify-between">
                    <div className="flex-1">
                      <h4 className="text-sm font-semibold text-ink">Essential Cookies</h4>
                      <p className="mt-1 text-xs text-ink-soft">
                        These help the website work properly
                      </p>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="text-xs text-ink-muted">Always on</span>
                      <div className="relative h-6 w-10 rounded-full bg-ink">
                        <div className="absolute right-1 top-1 h-4 w-4 rounded-full bg-paper-raised"></div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Analytics cookies */}
                <div className="rounded-control border border-line bg-paper-raised p-4">
                  <div className="flex items-center justify-between">
                    <div className="flex-1">
                      <h4 className="text-sm font-semibold text-ink">Analytics Cookies</h4>
                      <p className="mt-1 text-xs text-ink-soft">
                        Help us improve by showing how you use the site
                      </p>
                    </div>
                    <label className="relative inline-flex cursor-pointer items-center">
                      <input
                        type="checkbox"
                        checked={analyticsConsent}
                        onChange={(e) => setAnalyticsConsent(e.target.checked)}
                        className="peer sr-only"
                      />
                      <div className={TOGGLE}></div>
                    </label>
                  </div>
                </div>

                {/* Marketing cookies */}
                <div className="rounded-control border border-line bg-paper-raised p-4">
                  <div className="flex items-center justify-between">
                    <div className="flex-1">
                      <h4 className="text-sm font-semibold text-ink">Marketing Cookies</h4>
                      <p className="mt-1 text-xs text-ink-soft">
                        Help us show you relevant content
                      </p>
                    </div>
                    <label className="relative inline-flex cursor-pointer items-center">
                      <input
                        type="checkbox"
                        checked={marketingConsent}
                        onChange={(e) => setMarketingConsent(e.target.checked)}
                        className="peer sr-only"
                      />
                      <div className={TOGGLE}></div>
                    </label>
                  </div>
                </div>
              </div>

              <div className="mt-6 flex gap-3">
                <button onClick={handleClosePreferences} className={`min-h-[44px] flex-1 px-4 py-2 text-sm ${SECONDARY}`}>
                  Cancel
                </button>
                <button onClick={handleSavePreferences} className={`min-h-[44px] flex-1 px-4 py-2 text-sm ${PRIMARY}`}>
                  Save my choices
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
